import { useMutation, type UseMutationResult,useQueryClient } from '@tanstack/react-query';

import { PREORDER_STATUS_DISPLAY_LABEL, PreorderExternalStatus } from '@ekhodealer/ekho-common';
import { toast } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { preordersEndpoints } from '@config/server/apiEndpoints';

import { preorders } from './queryKeys';
import { updatePreorderStatusInCache } from './usePreorderCacheUtils';

export interface InvitePreorderParams {
	preorderId: string;
	buyerFullName?: string;
}

export interface BulkInvitePreordersParams {
	preorderIds: string[];
	buyerFullName?: string;
}

const invitePreorder = async (
	servicesProviderId: string,
	preorderId: string,
): Promise<void> => {
	await apiClient.post(preordersEndpoints.invite(servicesProviderId, preorderId));
};

export const useInvitePreorderToCheckout = (): UseMutationResult<void, Error, InvitePreorderParams> => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ preorderId }: InvitePreorderParams): Promise<void> => {
			if (!servicesProviderId) {
				throw new Error('Cannot invite preorder without services provider ID');
			}
			return invitePreorder(servicesProviderId, preorderId);
		},
		onSuccess: (_data, { preorderId, buyerFullName }) => {
			updatePreorderStatusInCache({
				queryClient,
				preorderId,
				newDisplayStatus: PREORDER_STATUS_DISPLAY_LABEL[PreorderExternalStatus.AWAITING_CHECKOUT],
			});
			const message = buyerFullName
				? `${buyerFullName} invited to checkout`
				: 'Preorder invited to checkout';
			toast({ message, variant: 'success' });
			// Delay invalidation — Algolia indexes asynchronously, so an immediate refetch
			// would return stale data and overwrite our cache update
			setTimeout(() => queryClient.invalidateQueries({ queryKey: preorders._def }), 3000);
		},
		onError: (error) => {
			toast({
				message: `Failed to invite preorder: ${error.message}`,
				variant: 'error',
			});
		},
	});
};

export const useBulkInvitePreordersToCheckout = (): UseMutationResult<void, Error, BulkInvitePreordersParams> => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ preorderIds }: BulkInvitePreordersParams): Promise<void> => {
			if (!servicesProviderId) {
				throw new Error('Cannot invite preorders without services provider ID');
			}
			const results = await Promise.allSettled(
				preorderIds.map((id) => invitePreorder(servicesProviderId, id)),
			);
			const failures = results.filter((r) => r.status === 'rejected');
			if (failures.length > 0) {
				throw new Error(`${failures.length} of ${preorderIds.length} invitations failed`);
			}
		},
		onSuccess: (_data, { preorderIds, buyerFullName }) => {
			for (const preorderId of preorderIds) {
				updatePreorderStatusInCache({
					queryClient,
					preorderId,
					newDisplayStatus: PREORDER_STATUS_DISPLAY_LABEL[PreorderExternalStatus.AWAITING_CHECKOUT],
				});
			}
			const message = preorderIds.length === 1 && buyerFullName
				? `${buyerFullName} invited to checkout`
				: `${preorderIds.length} preorder${preorderIds.length === 1 ? '' : 's'} invited to checkout`;
			toast({ message, variant: 'success' });
			// Delay invalidation — Algolia indexes asynchronously, so an immediate refetch
			// would return stale data and overwrite our cache update
			setTimeout(() => queryClient.invalidateQueries({ queryKey: preorders._def }), 3000);
		},
		onError: (error) => {
			toast({ message: error.message, variant: 'error' });
		},
	});
};
